'use client';
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Flex } from '@osmosis/tailwind-ui/components/Box';
import { P } from '@osmosis/tailwind-ui/components/Text';
import { textBlue } from '@osmosis/tailwind-ui/styles/text';
import { bg0 } from '@osmosis/tailwind-ui/styles';
import { FormattedMessage, useIntl } from 'react-intl';
import Link from 'next/link';

type SkipTarget = {
  linkText: string;
  /** data-testids in priority order; the first one present in the DOM is used. */
  linkUrl: string[];
};

type SkipLinkProps = {
  /** Single skip target (kept for backward compatibility). */
  linkText?: string;
  linkUrl?: string[];
  /**
   * Multiple skip targets rendered as a list, so keyboard users can Tab
   * between several destinations (e.g. the video and the AI assistant).
   * When provided, `linkText`/`linkUrl` are ignored. Targets with no
   * element in the DOM are not rendered.
   */
  links?: SkipTarget[];
  modalId?: string;
  style?: React.CSSProperties;
};

const findElementByTestId = (testId: string): HTMLElement | null =>
  document.querySelector<HTMLElement>(`[data-testid="${testId}"]`);

/**
 * A skip target that exists in the DOM is only a valid destination when it is
 * actually visible. A `display:none`/`hidden` element (e.g. the AI panel while
 * the user has it collapsed or hidden) cannot receive focus, so skipping to it
 * would land the user nowhere. Resolve to the next candidate instead.
 */
const isElementVisible = (el: HTMLElement): boolean => {
  if (el.hidden) {
    return false;
  }
  const { display, visibility } = window.getComputedStyle(el);
  return display !== 'none' && visibility !== 'hidden';
};

const findVisibleElementByTestId = (testId: string): HTMLElement | null => {
  const el = findElementByTestId(testId);
  return el && isElementVisible(el) ? el : null;
};

/**
 * Skip link component used for Accessibility. Supports a single destination
 * (`linkText`/`linkUrl`) or a list of destinations (`links`).
 * @param {SkipLinkProps} props - Props.
 */
const SkipLink = (props: SkipLinkProps) => {
  const { linkText, linkUrl, links, modalId = '', style = {} } = props; // linkUrl values are the data-testids of the elements to skip to
  const intl = useIntl();
  const [isVisible, setIsVisible] = useState(false);

  const targets: SkipTarget[] =
    links && links.length > 0
      ? links
      : [{ linkText: linkText ?? '', linkUrl: linkUrl ?? [] }];
  const targetsKey = JSON.stringify(targets);

  // First linkUrl of each target currently present in the DOM (null if none).
  // Starts empty so SSR and the first client render match.
  const [resolvedIds, setResolvedIds] = useState<(string | null)[]>([]);
  const availableTargets = targets.flatMap((target, index) => {
    const resolvedId = resolvedIds[index];
    return resolvedId ? [{ linkText: target.linkText, resolvedId }] : [];
  });
  const isMulti = availableTargets.length > 1;

  const firstTabPressedRef = useRef(false);
  // Outer positioning/visibility wrapper (used for focus containment checks).
  const containerRef = useRef<HTMLDivElement>(null);
  // First skip anchor, focused when the skip link is revealed.
  const firstItemRef = useRef<HTMLAnchorElement>(null);
  const observerRef = useRef<MutationObserver | null>(null);

  const handleScroll = useCallback((target: Element) => {
    target.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'nearest',
    });
  }, []);

  // Resolve each target to its first linkUrl present in the DOM, and keep it
  // in sync as the page renders (e.g. the AI panel mounting after hydration).
  useEffect(() => {
    const parsedTargets: SkipTarget[] = JSON.parse(targetsKey);
    let currentKey = '';
    const resolve = () => {
      const next = parsedTargets.map(
        ({ linkUrl: urls }) =>
          urls.find((url) => findVisibleElementByTestId(url) !== null) ?? null,
      );
      // Only update state on change; the observer fires on every DOM mutation.
      const nextKey = JSON.stringify(next);
      if (nextKey === currentKey) {
        return;
      }

      currentKey = nextKey;
      setResolvedIds(next);
    };

    resolve();
    const observer = new MutationObserver(resolve);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      // Show/hide is often a class or style toggle (e.g. the AI panel switching
      // between `flex` and `hidden`) with no element added/removed, so watch the
      // relevant attributes too or the resolved target would go stale.
      attributes: true,
      attributeFilter: ['class', 'hidden', 'style', 'aria-hidden'],
    });

    return () => {
      observer.disconnect();
    };
  }, [targetsKey]);

  const handleSkip = useCallback(
    (e: React.KeyboardEvent, targetId: string) => {
      if (
        (e as React.KeyboardEvent).key !== 'Enter' &&
        (e as React.KeyboardEvent).key !== ' '
      ) {
        return;
      }

      e.preventDefault();

      const targetElement = findVisibleElementByTestId(targetId);

      if (targetElement) {
        const originalTabIndex = targetElement.getAttribute('tabIndex');
        if (!targetElement.hasAttribute('tabIndex')) {
          targetElement.setAttribute('tabIndex', '-1');
        }
        targetElement.focus();
        handleScroll(targetElement);

        // Restore the original tabIndex after a short delay
        setTimeout(() => {
          if (originalTabIndex === null) {
            targetElement.removeAttribute('tabIndex');
          } else {
            targetElement.setAttribute('tabIndex', originalTabIndex);
          }
        }, 0);
      }
    },
    [handleScroll],
  );

  // this is to force close the skiplink when it shares the page with a modal/overlay
  useEffect(() => {
    // Without a modalId every DOM mutation would hide the skip link.
    if (!modalId) return;

    const checkForModal = () => {
      const modalElement = document.querySelector(`[data-testid="${modalId}"]`);
      if (!modalElement) {
        setIsVisible(false);
      }
    };

    checkForModal();
    observerRef.current = new MutationObserver(checkForModal);
    observerRef.current.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      observerRef.current?.disconnect();
    };
  }, [modalId]);

  useEffect(() => {
    // Only the very first forward Tab reveals the skip link, and not while the
    // user is tabbing out of a text field.
    const shouldRevealOnTab = (e: KeyboardEvent) => {
      if (firstTabPressedRef.current || e.shiftKey) {
        return false;
      }

      const { tagName } = document.activeElement as HTMLElement;
      return tagName !== 'INPUT' && tagName !== 'TEXTAREA';
    };

    const containsFocus = (node: Node | null) =>
      !!containerRef.current &&
      (containerRef.current === node || containerRef.current.contains(node));

    const handleKeyDown = (e: KeyboardEvent) => {
      // Nothing rendered (no target available), so don't hijack Tab.
      if (e.key !== 'Tab' || !firstItemRef.current) {
        return;
      }

      if (!shouldRevealOnTab(e)) {
        setIsVisible(false);
        return;
      }

      e.preventDefault(); // Prevent default tabbing behavior
      setIsVisible(true);
      firstItemRef.current.focus();
      firstTabPressedRef.current = true;
    };

    const handleFocus = (e: FocusEvent) => {
      if (!firstTabPressedRef.current) {
        return;
      }

      setIsVisible(containsFocus(e.target as Node | null));
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('focusin', handleFocus);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('focusin', handleFocus);
    };
  }, []);

  if (availableTargets.length === 0) {
    return null;
  }

  // Single target: the skip anchor is the interactive element (no wrapping
  // role="button", which would nest one interactive control inside another).
  if (!isMulti) {
    const [target] = availableTargets;
    return (
      <Flex
        ref={containerRef}
        className={`skip-link ml-2 absolute top-12 left-24 ${bg0} p-2 border border-black rounded-md z-[99999] ${
          isVisible
            ? 'pointer-events-auto skip-link--visible top-[50px] left-[100px]'
            : ' pointer-events-none top-[-1000px] left-[-1000px]'
        }`}
        style={{
          opacity: isVisible ? 1 : 0, // Hide visually when not visible
          ...style,
        }}
        data-testid="skip-link"
      >
        <Link
          href={`#${target.resolvedId}`}
          data-testid="accessibility-skip-link"
          ref={firstItemRef}
          onKeyDown={(e) => handleSkip(e, target.resolvedId)}
        >
          <P className={`${textBlue} font-bold underline cursor-pointer`}>
            <FormattedMessage
              id="sidenav.skip.link"
              defaultMessage="Skip to {linkText}"
              values={{ linkText: target.linkText }}
            />
          </P>
        </Link>
      </Flex>
    );
  }

  // Multiple targets: a list of skip anchors the user can Tab between.
  return (
    <Flex
      ref={containerRef}
      aria-label={intl.formatMessage({
        id: 'sidenav.skip.links.label',
        defaultMessage: 'Skip links',
      })}
      className={`skip-link ml-2 absolute flex-col gap-2 ${bg0} p-2 border border-black rounded-md z-[99999] ${
        isVisible
          ? 'pointer-events-auto skip-link--visible top-[50px] left-[100px]'
          : ' pointer-events-none top-[-1000px] left-[-1000px]'
      }`}
      style={{
        opacity: isVisible ? 1 : 0,
        ...style,
      }}
      data-testid="skip-links"
    >
      {availableTargets.map((target, index) => (
        <Link
          key={`${target.resolvedId}-${index}`}
          href={`#${target.resolvedId}`}
          ref={index === 0 ? firstItemRef : undefined}
          data-testid={
            index === 0
              ? 'accessibility-skip-link'
              : `accessibility-skip-link-${index}`
          }
          onKeyDown={(e) => handleSkip(e, target.resolvedId)}
        >
          <P className={`${textBlue} font-bold underline cursor-pointer`}>
            <FormattedMessage
              id="sidenav.skip.link"
              defaultMessage="Skip to {linkText}"
              values={{ linkText: target.linkText }}
            />
          </P>
        </Link>
      ))}
    </Flex>
  );
};

export default SkipLink;
